import type { TQuotationDeliveryDate } from "./QuotationConfiguration"

// A row is "half filled" when exactly one of date/name is set. The API accepts an empty project
// name, but the UI requires both together or neither — see AGENTS.md / REQ-407..410.
export const isDeliveryDateIncomplete = (d: TQuotationDeliveryDate): boolean => !!d.date !== !!d.name?.trim()

export const hasIncompleteDeliveryDate = (deliveryDates: TQuotationDeliveryDate[]): boolean =>
	deliveryDates.some(isDeliveryDateIncomplete)

// `key` is a client-only identity, stable across edits — never derived from array position (an
// index-keyed row breaks React's reconciliation once a row is removed from the middle: the DOM
// node currently focused silently gets re-mapped to a different row's data).
export const createEmptyDeliveryDate = (): TQuotationDeliveryDate => ({
	key: crypto.randomUUID(),
	id: null,
})

/** A delivery-date row that carries a date, i.e. one the API can be told about. */
type TDatedDeliveryDate = TQuotationDeliveryDate & { date: string }

const isDated = (d: TQuotationDeliveryDate): d is TDatedDeliveryDate => !!d.date

/**
 * The `delivery_dates` half of the configuration PATCH body — a partial, so an empty list is
 * OMITTED rather than sent as `[]` (issue #1341).
 *
 * `PATCH /api/v1/quotations/{id}/` documents the field as "the full current state of delivery dates
 * for the quotation **only when that field is being updated**", and the list carries a backend
 * `minItems: 1`. Sending `[]` is therefore not "no dates", it is an invalid update, and it answers
 * with the exact error the issue reports: "Delivery dates — Non field errors: Ensure this field has
 * at least 1 elements". Omitting the key is what "no delivery date was provided" actually looks like
 * on the wire. `CreateQuotationModal` already builds its POST body the same way.
 *
 * `project_name` is likewise omitted when blank rather than sent empty: the endpoint states
 * "Omitting it keeps the stored name unchanged. A project name cannot be removed once it is set."
 */
export const buildDeliveryDatesPayload = (
	deliveryDates: TQuotationDeliveryDate[],
): { delivery_dates?: { id: number | null; date: string; project_name?: string }[] } => {
	const rows = deliveryDates
		.filter(isDated)
		.map((d) => ({ id: d.id, date: d.date, ...(d.name?.trim() ? { project_name: d.name.trim() } : {}) }))
	return rows.length > 0 ? { delivery_dates: rows } : {}
}

/**
 * True when the form has dropped every delivery date the quotation had stored.
 *
 * This state is not expressible through the API at all: omitting `delivery_dates` means "leave them
 * alone" (so the removal would be silently discarded) and sending `[]` fails the `minItems: 1`
 * validator, while no delete endpoint for a delivery date exists. So the UI has to say so itself
 * rather than fire a request that cannot succeed — see the spec's AC16. Removing SOME of several
 * saved dates is unaffected: the remaining rows are sent as the full current state.
 */
export const hasClearedSavedDeliveryDates = (
	savedDeliveryDateCount: number,
	deliveryDates: TQuotationDeliveryDate[],
): boolean => savedDeliveryDateCount > 0 && !deliveryDates.some(isDated)
